import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { setResponsibleBuyer } from '../../../shared/api/requests.api'
import { getProcurementUsers } from '../../../shared/api/users.api'
import { toast } from '../../../shared/ui/toastStore'

type Buyer = { id: string; name?: string | null; email: string }

type Props = {
  requestId: string
  current: Buyer | null
  /** Кому разрешено менять (совпадает с ролями роута на бэке). */
  canEdit: boolean
}

/**
 * «Закупщик проекта» (решение Р12) — закрепление ответственного за проектом.
 *
 * Бэкенд-ручка `PATCH /requests/:id/responsible-buyer` существовала с 13.07,
 * но её не вызывал ни один компонент: в шапке заявки закупщик только
 * показывался с подписью «правятся в карточке клиента», а в карточке клиента
 * правки тоже не было — фича была недостижима из интерфейса.
 */
export function ResponsibleBuyerPicker({ requestId, current, canEdit }: Props) {
  const qc = useQueryClient()
  const [open, setOpen] = useState(false)

  const buyersQ = useQuery({
    queryKey: ['users', 'procurement'],
    queryFn: getProcurementUsers,
    enabled: open,
  })

  const mutation = useMutation({
    mutationFn: (buyerId: string | null) => setResponsibleBuyer(requestId, buyerId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['request', requestId] })
      void qc.invalidateQueries({ queryKey: ['requests'] })
      setOpen(false)
      toast.success('Закупщик проекта обновлён')
    },
    onError: (e: unknown) => {
      const msg = (e as { response?: { data?: { error?: { message?: string } } } }).response?.data
        ?.error?.message
      toast.error(msg ?? 'Не удалось изменить закупщика проекта')
    },
  })

  const label = current ? current.name || current.email : 'не назначен'

  if (!canEdit) {
    return (
      <span className="ml-2">
        <span className="text-gray-400">· Закупщик проекта:</span> {label}
      </span>
    )
  }

  return (
    <span className="ml-2 inline-flex items-center gap-1">
      <span className="text-gray-400">· Закупщик проекта:</span>
      {open ? (
        <select
          autoFocus
          disabled={mutation.isPending || buyersQ.isLoading}
          value={current?.id ?? ''}
          onChange={(e) => mutation.mutate(e.target.value === '' ? null : e.target.value)}
          onBlur={() => setOpen(false)}
          className="rounded-lg border border-gray-300 px-2 py-0.5 text-sm focus:outline-none focus:ring-1 focus:ring-brand-500"
        >
          <option value="">— не назначен —</option>
          {(buyersQ.data ?? []).map((u) => (
            <option key={u.id} value={u.id}>
              {u.name || u.email}
            </option>
          ))}
        </select>
      ) : (
        <button
          type="button"
          onClick={() => setOpen(true)}
          disabled={mutation.isPending}
          className="rounded-lg px-1.5 py-0.5 text-sm text-brand-600 hover:bg-brand-50 disabled:opacity-40"
          title="Назначить закупщика проекта"
        >
          {mutation.isPending ? 'Сохраняю…' : label}
        </button>
      )}
    </span>
  )
}
